/**
 * Staff view (issue 1206): a platform admin works inside a partner
 * organization's portal, the way its staff see it, in one browser tab.
 *
 * The API hands out a signed token tied to the admin's own session. This tab
 * keeps it in sessionStorage (so other tabs, including the admin's home-org
 * portal tab, never see it) and sends it as the X-RSWorks-Staff-View header on
 * every API call. A tab with no token behaves exactly as before: nothing is
 * read, added, or changed.
 *
 * The token arrives in the URL fragment (#staff-view=<token>), which never
 * reaches a server or a referrer. It is lifted out of the URL as soon as this
 * module loads, before routing or telemetry read the location.
 */
import { useEffect, useSyncExternalStore } from 'react';
import { AUTH0_APP_LOGOUT_EVENT } from '../auth/auth0Session';

export const STAFF_VIEW_HEADER = 'X-RSWorks-Staff-View';
export const STAFF_VIEW_STORAGE_KEY = 'rsworks.staffView.v1';
export const STAFF_VIEW_FRAGMENT_KEY = 'staff-view';
export const STAFF_VIEW_ENDED_EVENT = 'rsworks:staff-view-ended';
export const STAFF_VIEW_CHANGED_EVENT = 'rsworks:staff-view-changed';
export const STAFF_VIEW_ERROR_CODES = Object.freeze(['STAFF_VIEW_INVALID', 'STAFF_VIEW_NOT_ALLOWED']);
// Same-origin tabs that hold the same token (the caseload tab and the edit on
// behalf tabs it opened) hear each other's Exit here.
export const STAFF_VIEW_CHANNEL = 'rsworks:staff-view';

// setTimeout overflows past ~24.8 days; tokens live eight hours.
const MAX_TIMER_MS = 2147483647;
// Longest a staff view waits for pending work (queued resume saves) before it
// ends anyway: a slow or stuck save never traps the admin in staff view.
const BEFORE_EXIT_WAIT_MS = 15000;

// Why this tab's staff view stopped ({ reason, organizationName }), kept for
// the life of the page so the portal can say so instead of quietly showing
// the admin's own organization. A reload starts clean.
let endedState = null;
let changeVersion = 0;

function sessionStore() {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage || null;
  } catch {
    return null;
  }
}

function readRaw() {
  try {
    return sessionStore()?.getItem(STAFF_VIEW_STORAGE_KEY) ?? null;
  } catch {
    return null;
  }
}

function writeRaw(value) {
  try {
    const store = sessionStore();
    if (!store) return;
    if (value === null) store.removeItem(STAFF_VIEW_STORAGE_KEY);
    else store.setItem(STAFF_VIEW_STORAGE_KEY, value);
  } catch {
    /* storage blocked: staff view simply is not available in this tab */
  }
}

function parseEntry(raw) {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    if (!value || typeof value.token !== 'string' || !value.token) return null;
    return value;
  } catch {
    return null;
  }
}

function isExpired(entry, now = Date.now()) {
  if (!entry?.expiresAt) return false;
  const at = Date.parse(entry.expiresAt);
  return Number.isFinite(at) && at <= now;
}

function notifyChange() {
  changeVersion += 1;
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent(STAFF_VIEW_CHANGED_EVENT));
  } catch {
    /* never break the caller */
  }
}

function base64UrlDecode(value) {
  const normalized = String(value).replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const bytes = Uint8Array.from(binary, (ch) => ch.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

/**
 * Read the organization and expiry from a token's payload, for display and
 * client-side expiry only. The server verifies the signature on every call.
 * @returns {{ organizationId: string|null, expiresAt: string|null } | null}
 */
export function decodeStaffViewToken(token) {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3 || parts[0] !== 'sv1') return null;
  try {
    const payload = JSON.parse(base64UrlDecode(parts[1]));
    const exp = Number(payload?.exp);
    return {
      organizationId: typeof payload?.org === 'string' ? payload.org : null,
      expiresAt: Number.isFinite(exp) ? new Date(exp).toISOString() : null,
    };
  } catch {
    return null;
  }
}

/** The stored entry while it is live, without side effects (safe in render). */
function readLiveEntry() {
  const entry = parseEntry(readRaw());
  if (!entry || isExpired(entry)) return null;
  return entry;
}

// --- Sibling tabs ----------------------------------------------------------
// Only a tab that holds a token opens the channel, so a tab with no staff view
// never listens or posts. Messages carry the token's signature segment, never
// the whole token, and a tab acts only when that matches its own token.

let channel = null;
const beforeExitHooks = new Set();

function tokenKey(token) {
  const parts = String(token || '').split('.');
  return parts.length === 3 ? parts[2] : String(token || '');
}

function closeChannel() {
  if (!channel) return;
  const open = channel;
  channel = null;
  try {
    open.close();
  } catch {
    /* already closed */
  }
}

function ensureChannel() {
  if (channel || typeof window === 'undefined' || typeof window.BroadcastChannel !== 'function') return;
  try {
    channel = new window.BroadcastChannel(STAFF_VIEW_CHANNEL);
    channel.onmessage = (event) => {
      void endFromSiblingExit(event?.data);
    };
  } catch {
    channel = null;
  }
}

function announceExit(token) {
  try {
    channel?.postMessage({ type: 'exit', key: tokenKey(token) });
  } catch {
    /* the other tabs find out on their next request */
  }
}

/**
 * Wait for pending work before a staff view ends. The wait is capped. Returns
 * `{ ok: false }` when a hook failed or did not finish in time, so the caller
 * can keep the staff view open instead of losing unsaved work.
 * @param {Array<() => unknown>} hooks
 * @returns {Promise<{ ok: boolean }>}
 */
export async function settleBeforeStaffViewExit(hooks, timeoutMs = BEFORE_EXIT_WAIT_MS) {
  const pending = (hooks || []).filter((hook) => typeof hook === 'function');
  if (pending.length === 0) return { ok: true };
  let timer = null;
  try {
    const outcome = await Promise.race([
      Promise.allSettled(pending.map((hook) => Promise.resolve().then(() => hook()))),
      new Promise((resolve) => { timer = setTimeout(() => resolve(null), timeoutMs); }),
    ]);
    if (!outcome) return { ok: false };
    return { ok: outcome.every((result) => result.status === 'fulfilled') };
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Run `hook` (e.g. flush queued resume saves) before an Exit in a sibling tab
 * ends this tab's staff view, while requests still carry the header.
 * @returns {() => void} unregister
 */
export function registerStaffViewBeforeExit(hook) {
  if (typeof hook !== 'function') return () => {};
  beforeExitHooks.add(hook);
  return () => { beforeExitHooks.delete(hook); };
}

async function endFromSiblingExit(message) {
  if (!message || message.type !== 'exit' || typeof message.key !== 'string') return;
  const entry = parseEntry(readRaw());
  if (!entry || tokenKey(entry.token) !== message.key) return;
  const { ok } = await settleBeforeStaffViewExit([...beforeExitHooks]);
  // Unsaved work here: keep this tab in the staff view so it can be saved.
  if (!ok) return;
  // Signed out or reopened meanwhile: nothing of this staff view is left to end.
  if (parseEntry(readRaw())?.token !== entry.token) return;
  endStaffView('exit', { broadcast: false });
}

/**
 * Stop this tab's staff view: drop the token, remember why, and tell the app.
 * Returns false when there was nothing to end, so a burst of rejected
 * requests ends it once. An Exit also ends the sibling tabs holding the same
 * token.
 * @param {'invalid' | 'expired' | 'exit'} reason
 * @param {{ broadcast?: boolean }} [options]
 */
export function endStaffView(reason = 'invalid', { broadcast = reason === 'exit' } = {}) {
  const entry = parseEntry(readRaw());
  if (!entry) return false;
  writeRaw(null);
  if (broadcast) announceExit(entry.token);
  closeChannel();
  endedState = { reason, organizationName: entry.organizationName || '' };
  notifyChange();
  if (typeof window !== 'undefined') {
    try {
      window.dispatchEvent(new CustomEvent(STAFF_VIEW_ENDED_EVENT, { detail: { ...endedState } }));
    } catch {
      /* never break the caller */
    }
  }
  return true;
}

/**
 * The live staff view for this tab, or null. An expired entry is dropped here
 * (and the end is announced), so callers never act on a stale token.
 */
export function getStaffView() {
  const entry = parseEntry(readRaw());
  if (!entry) return null;
  if (isExpired(entry)) {
    endStaffView('expired');
    return null;
  }
  return entry;
}

/** True when this tab is working in staff view (side-effect free). */
export function isStaffViewActive() {
  return Boolean(readLiveEntry());
}

/**
 * Store a staff view for this tab.
 * @param {{ token: string, expiresAt?: string|null, organizationId?: string|null,
 *   organizationName?: string, organizationStatus?: string }} value
 */
export function setStaffView(value) {
  if (!value || typeof value.token !== 'string' || !value.token) return null;
  const decoded = decodeStaffViewToken(value.token);
  const entry = {
    token: value.token,
    expiresAt: value.expiresAt || decoded?.expiresAt || null,
    organizationId: value.organizationId || decoded?.organizationId || null,
    organizationName: value.organizationName || '',
  };
  if (value.organizationStatus) entry.organizationStatus = value.organizationStatus;
  writeRaw(JSON.stringify(entry));
  ensureChannel();
  endedState = null;
  notifyChange();
  return entry;
}

/** Forget this tab's staff view without announcing an end. */
export function clearStaffView() {
  const had = readRaw() !== null;
  writeRaw(null);
  closeChannel();
  if (had) notifyChange();
}

/** Forget that a staff view ended in this tab (the ended notice goes away). */
export function dismissStaffViewEnded() {
  if (!endedState) return;
  endedState = null;
  notifyChange();
}

/**
 * Sign-out (this tab, a peer tab, or an expired session): the token was tied
 * to that session, so drop it quietly. No "staff view ended" notice; the app
 * shows its normal signed-out screen.
 */
export function clearStaffViewOnSignOut() {
  clearStaffView();
  dismissStaffViewEnded();
}

/**
 * `#staff-view=<token>` while this tab is in staff view, otherwise ''.
 * Side-effect free, so it is safe to call while rendering an href.
 */
export function staffViewFragment() {
  const entry = readLiveEntry();
  return entry ? `#${STAFF_VIEW_FRAGMENT_KEY}=${encodeURIComponent(entry.token)}` : '';
}

/**
 * Append the staff-view fragment to an in-app href that opens in a new tab,
 * so that tab keeps working in the same organization. Unchanged otherwise.
 */
export function withStaffViewFragment(href) {
  if (!href) return href;
  const fragment = staffViewFragment();
  if (!fragment || String(href).includes('#')) return href;
  return `${href}${fragment}`;
}

/**
 * Lift `#staff-view=<token>` out of the current URL into this tab's storage,
 * keeping the path and query (and any other fragment params).
 * @returns {boolean} whether a token was found
 */
export function bootstrapStaffViewFromLocation(win = typeof window !== 'undefined' ? window : undefined) {
  const location = win?.location;
  const hash = location?.hash || '';
  if (!hash.includes(`${STAFF_VIEW_FRAGMENT_KEY}=`)) return false;

  const params = new URLSearchParams(hash.slice(1));
  const token = params.get(STAFF_VIEW_FRAGMENT_KEY);
  params.delete(STAFF_VIEW_FRAGMENT_KEY);
  const rest = params.toString();
  try {
    win.history.replaceState(
      win.history.state,
      '',
      `${location.pathname}${location.search}${rest ? `#${rest}` : ''}`,
    );
  } catch {
    /* the token is still stored; only the address bar keeps it */
  }

  if (!token) return false;
  setStaffView({ token });
  return true;
}

// --- HTTP wiring ---------------------------------------------------------

const ABSOLUTE_URL = /^([a-z][a-z\d+.-]*:)?\/\//i;

/**
 * Only RS Works API calls carry the header, never a third-party URL. The URL is
 * resolved the way axios and the browser will (baseURL joined to a relative
 * path; backslashes and protocol-relative forms included) and its origin must
 * be this page's or the API's.
 */
function targetsRsworksApi(config) {
  if (typeof window === 'undefined') return false;
  const url = String(config?.url || '');
  try {
    const page = window.location.href;
    const allowed = new Set([window.location.origin]);
    let effective = url;
    if (config?.baseURL) {
      const base = new URL(config.baseURL, page);
      allowed.add(base.origin);
      if (!ABSOLUTE_URL.test(url)) {
        effective = `${String(config.baseURL).replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}`;
      }
    }
    return allowed.has(new URL(effective, page).origin);
  } catch {
    return false;
  }
}

function readHeader(config) {
  const headers = config?.headers;
  if (!headers) return null;
  if (typeof headers.get === 'function') {
    const value = headers.get(STAFF_VIEW_HEADER);
    if (value) return String(value);
  }
  return headers[STAFF_VIEW_HEADER] || headers[STAFF_VIEW_HEADER.toLowerCase()] || null;
}

function writeHeader(config, value) {
  if (config.headers && typeof config.headers.set === 'function') {
    config.headers.set(STAFF_VIEW_HEADER, value);
  } else {
    config.headers = { ...(config.headers || {}), [STAFF_VIEW_HEADER]: value };
  }
}

function requestPath(config) {
  const url = String(config?.url || '').split('?')[0];
  if (ABSOLUTE_URL.test(url)) {
    try {
      return new URL(url, 'http://localhost').pathname;
    } catch {
      return url;
    }
  }
  return url;
}

function isMeRequest(config) {
  const method = String(config?.method || 'get').toLowerCase();
  return method === 'get' && /(^|\/)auth\/me\/?$/.test(requestPath(config));
}

/** Fill the organization fields from GET /auth/me (`user.staffView`). */
function syncFromMe(staffView, sentToken) {
  const entry = parseEntry(readRaw());
  if (!entry || entry.token !== sentToken || !staffView || typeof staffView !== 'object') return;
  const next = {
    ...entry,
    organizationId: staffView.organizationId || entry.organizationId || null,
    organizationName: staffView.organizationName || entry.organizationName || '',
    expiresAt: staffView.expiresAt || entry.expiresAt || null,
  };
  if (staffView.organizationStatus) next.organizationStatus = staffView.organizationStatus;
  const raw = JSON.stringify(next);
  if (raw === JSON.stringify(entry)) return;
  writeRaw(raw);
  notifyChange();
}

function staffViewEndedError(config) {
  const error = new Error('Staff view has ended.');
  error.code = 'STAFF_VIEW_ENDED';
  error.config = config;
  error.rsworksStaffViewEnded = true;
  return error;
}

const patchedClients = new WeakSet();

/**
 * Add the staff-view header to an axios client's API calls while this tab has
 * a live token, and end the staff view when the server rejects it. With no
 * token stored, requests and responses pass through untouched.
 */
export function attachStaffViewInterceptors(client) {
  if (!client?.interceptors || patchedClients.has(client)) return;
  patchedClients.add(client);

  client.interceptors.request.use((config) => {
    const entry = parseEntry(readRaw());
    if (!entry || !targetsRsworksApi(config)) return config;
    if (isExpired(entry)) {
      // The portal shows "staff view ended" from here on. Only the session
      // check goes out as the admin's own; every other request stops here, so
      // the admin's own organization never shows up inside this staff view and
      // nothing lands in the wrong organization.
      endStaffView('expired');
      if (!isMeRequest(config)) throw staffViewEndedError(config);
      return config;
    }
    writeHeader(config, entry.token);
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      const sentToken = readHeader(response?.config);
      if (sentToken && isMeRequest(response.config)) {
        if (response.data?.authenticated === false) {
          // Signed out: quietly drop the token that went with that session
          // (unless this tab has started a new staff view meanwhile).
          if (parseEntry(readRaw())?.token === sentToken) clearStaffViewOnSignOut();
        } else if (response.data?.staffViewError) {
          if (parseEntry(readRaw())?.token === sentToken) endStaffView('invalid');
        } else if (response.data?.user?.staffView) {
          syncFromMe(response.data.user.staffView, sentToken);
        }
      }
      return response;
    },
    (error) => {
      const code = error?.response?.data?.code;
      const sentToken = readHeader(error?.config);
      if (
        error?.response?.status === 403
        && STAFF_VIEW_ERROR_CODES.includes(code)
        && sentToken
      ) {
        // A late answer for an earlier staff view must not end the current one.
        if (parseEntry(readRaw())?.token === sentToken) endStaffView('invalid');
        Object.assign(error, { rsworksStaffViewEnded: true });
      }
      return Promise.reject(error);
    },
  );
}

// --- React ---------------------------------------------------------------

const INACTIVE = Object.freeze({
  active: false,
  token: null,
  organizationId: null,
  organizationName: '',
  organizationStatus: null,
  expiresAt: null,
  ended: null,
});

let snapshotKey = null;
let snapshotValue = INACTIVE;

function getSnapshot() {
  const raw = readRaw();
  const entry = parseEntry(raw);
  const live = Boolean(entry) && !isExpired(entry);
  const key = `${changeVersion}|${live ? raw : ''}`;
  if (key === snapshotKey) return snapshotValue;
  snapshotKey = key;
  if (!live && !endedState) {
    snapshotValue = INACTIVE;
  } else if (!live) {
    snapshotValue = Object.freeze({ ...INACTIVE, ended: endedState });
  } else {
    snapshotValue = Object.freeze({
      active: true,
      token: entry.token,
      organizationId: entry.organizationId || null,
      organizationName: entry.organizationName || '',
      organizationStatus: entry.organizationStatus || null,
      expiresAt: entry.expiresAt || null,
      ended: null,
    });
  }
  return snapshotValue;
}

function getServerSnapshot() {
  return INACTIVE;
}

function subscribe(callback) {
  if (typeof window === 'undefined') return () => {};
  const onStorage = (event) => {
    if (event.key !== null && event.key !== STAFF_VIEW_STORAGE_KEY) return;
    changeVersion += 1;
    callback();
  };
  window.addEventListener(STAFF_VIEW_CHANGED_EVENT, callback);
  window.addEventListener(STAFF_VIEW_ENDED_EVENT, callback);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(STAFF_VIEW_CHANGED_EVENT, callback);
    window.removeEventListener(STAFF_VIEW_ENDED_EVENT, callback);
    window.removeEventListener('storage', onStorage);
  };
}

/**
 * This tab's staff view: `{ active, token, organizationId, organizationName,
 * organizationStatus, expiresAt, ended }`. Re-renders when it starts, is
 * filled in from /me, expires, or ends.
 */
export function useStaffView() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const { active, expiresAt } = snapshot;

  useEffect(() => {
    if (!active || !expiresAt) return undefined;
    const remaining = Date.parse(expiresAt) - Date.now();
    if (!Number.isFinite(remaining)) return undefined;
    const handle = setTimeout(() => { getStaffView(); }, Math.min(Math.max(remaining, 0) + 50, MAX_TIMER_MS));
    return () => clearTimeout(handle);
  }, [active, expiresAt]);

  return snapshot;
}

// Runs once, when src/index.js (via the API client) first loads this module:
// before App, routing, or telemetry read the URL. A reload keeps the tab's
// token (sessionStorage), so the sibling channel reopens here too.
bootstrapStaffViewFromLocation();
if (parseEntry(readRaw())) ensureChannel();
// AuthContext announces its own sign-out and a peer tab's on this event.
if (typeof window !== 'undefined') {
  window.addEventListener(AUTH0_APP_LOGOUT_EVENT, clearStaffViewOnSignOut);
}
